import { mergePdfFiles } from "../utils/mergePdf";
import { storeMergedPdf, getPendingFiles, clearPendingFiles } from "../utils/store";

export const MAX_FILES = 6;
export const MAX_FILE_BYTES = 85 * 1024 * 1024;

interface SelectedFile {
  id: string;
  file: File;
}

export async function renderEditor(
  root: HTMLElement,
  onMerge: () => void,
  onBack: () => void
): Promise<void> {
  const files: SelectedFile[] = [];

  root.innerHTML = `
    <header class="app-header app-header--compact">
      <button class="app-header__back" data-action="back" aria-label="Back">&#8249;</button>
      <div class="app-header__inner">
        <h1 class="app-header__title">Merge PDF</h1>
      </div>
    </header>

    <main class="page">
      <input
        type="file"
        accept="application/pdf"
        multiple
        data-action="picker"
        style="display:none"
      />

      <div class="select-area">
        <button class="btn-primary btn-large" data-action="add">Select Files (Minimum 2)</button>
      </div>

      <ul class="file-list" data-role="list"></ul>

      <p class="error-text" data-role="error" hidden></p>

      <div class="merge-area">
        <button
          class="btn-primary btn-large"
          data-action="merge"
          disabled
        >Start Merge</button>
      </div>

      <div class="loading-overlay" data-role="loading" hidden>
        <div class="loading-spinner"></div>
        <p class="loading-text">Merging…</p>
      </div>
    </main>
  `;

  const listEl = root.querySelector<HTMLElement>('[data-role="list"]')!;
  const errorEl = root.querySelector<HTMLElement>('[data-role="error"]')!;
  const loadingEl = root.querySelector<HTMLElement>('[data-role="loading"]')!;
  const mergeBtn = root.querySelector<HTMLButtonElement>('[data-action="merge"]')!;
  const addBtn = root.querySelector<HTMLButtonElement>('[data-action="add"]')!;
  const picker = root.querySelector<HTMLInputElement>('[data-action="picker"]')!;

  function setError(msg: string | null) {
    if (msg) {
      errorEl.textContent = msg;
      errorEl.hidden = false;
    } else {
      errorEl.hidden = true;
      errorEl.textContent = "";
    }
  }

  function rerender() {
    listEl.innerHTML = files
      .map(
        (f) => `
        <li class="file-item" data-id="${f.id}">
          <div class="file-item__icon">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <rect width="28" height="28" rx="4" fill="#F27733"/>
              <text x="14" y="18" text-anchor="middle" font-family="sans-serif" font-size="7" font-weight="700" fill="#fff">PDF</text>
            </svg>
          </div>
          <span class="file-item__name" title="${escapeHtml(f.file.name)}">${escapeHtml(f.file.name)}</span>
          <button class="file-item__remove" data-action="remove" data-id="${f.id}">Remove</button>
        </li>
      `
      )
      .join("");

    mergeBtn.disabled = files.length < 2;
    addBtn.disabled = files.length >= MAX_FILES;
  }

  picker.addEventListener("change", () => {
    const picked = Array.from(picker.files ?? []);
    let reachedLimit = false;
    for (const file of picked) {
      if (files.length >= MAX_FILES) {
        reachedLimit = true;
        break;
      }
      if (file.type !== "application/pdf") {
        setError(`${file.name} is not a PDF`);
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        setError(`${file.name} exceeds 85MB limit`);
        continue;
      }
      setError(null);
      files.push({ id: crypto.randomUUID(), file });
    }
    if (reachedLimit) {
      setError(`Maximum ${MAX_FILES} files allowed. Remove some files to add more.`);
    }
    picker.value = "";
    rerender();
  });

  addBtn.addEventListener("click", () => picker.click());

  listEl.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    if (target.dataset.action !== "remove") return;
    const id = target.dataset.id;
    const idx = files.findIndex((f) => f.id === id);
    if (idx >= 0) files.splice(idx, 1);
    setError(null);
    rerender();
  });

  mergeBtn.addEventListener("click", async () => {
    if (files.length < 2) return;
    setError(null);
    mergeBtn.disabled = true;
    addBtn.disabled = true;
    loadingEl.hidden = false;

    try {
      const { blob, sizeBytes, pageCount } = await mergePdfFiles(
        files.map((f) => f.file)
      );

      const ts = new Date();
      const stamp = `${ts.getFullYear()}${pad(ts.getMonth() + 1)}${pad(ts.getDate())}-${pad(ts.getHours())}${pad(ts.getMinutes())}`;
      storeMergedPdf(blob, {
        fileName: `merged-${stamp}.pdf`,
        sizeBytes,
        pageCount,
        sourceCount: files.length,
        createdAt: Date.now(),
      });
      onMerge();
    } catch (err) {
      setError((err as Error).message || "Merge failed");
      mergeBtn.disabled = false;
      addBtn.disabled = false;
    } finally {
      loadingEl.hidden = true;
    }
  });

  root
    .querySelector<HTMLButtonElement>('[data-action="back"]')!
    .addEventListener("click", onBack);

  const pending = getPendingFiles();
  if (pending.length > 0) {
    for (const file of pending) {
      if (files.length >= MAX_FILES) break;
      if (file.type === "application/pdf" && file.size <= MAX_FILE_BYTES) {
        files.push({ id: crypto.randomUUID(), file });
      }
    }
    clearPendingFiles();
  }

  rerender();
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case '"': return "&quot;";
      case "'": return "&#39;";
      default: return c;
    }
  });
}
